import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Clock, 
  Target, 
  Calendar,
  Award,
  Activity,
  ChevronDown,
  BarChart3,
  Zap,
  TrendingUp,
  TrendingDown
} from "lucide-react";
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, Legend, Bar, BarChart } from "recharts";
import ErrorMessage from "../../../components/ErrorMessage.js";

const SessionInsights = ({ userId, discipline }) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [timePeriod, setTimePeriod] = useState('month');
  const [selectedMetric, setSelectedMetric] = useState('skills');
  const [showTable, setShowTable] = useState(false);

  useEffect(() => {
    const endpoint = `/api/insights/sessions/${discipline}/${userId}?period=${timePeriod}`;
    
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(process.env.REACT_APP_API_HOST + endpoint);
        setInsights(response.data);
      } catch (error) {
        console.error('Error fetching insights:', error);
        setIsError(true);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [userId, discipline, timePeriod]);

  if (isError) return <ErrorMessage handleConfirm={() => setIsError(false)} />;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading session insights...</p>
        </div>
      </div>
    );
  }

  if (!insights || insights.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl p-6 shadow-lg text-center max-w-sm">
          <Calendar className="text-gray-400 mx-auto mb-4" size={48} />
          <h3 className="text-lg font-bold text-gray-900 mb-2">No Data Yet</h3>
          <p className="text-sm text-gray-600">Start logging sessions to see your insights here!</p>
        </div>
      </div>
    );
  }

  // // Calculate summary stats
  // const totalSessions = insights ? insights.reduce((sum, s) => sum + (s.count || 0), 0) : 0;
  // const totalAttempts = insights ? insights.reduce((sum, s) => sum + (s.total_attempts || 0), 0) : 0;
  // const totalSkills = insights ? insights.reduce((sum, s) => sum + (s.total_skills || 0), 0) : 0;
  // const avgHours = insights ? insights.reduce((sum, s) => sum + (s.total_minutes || 0), 0) / totalSessions : 0;
  // const avgSkills = insights ? parseInt(insights.reduce((sum, s) => sum + (s.total_skills || 0), 0) / totalSessions) : 0;
  // const avgCompletion = insights.reduce((sum, s) => sum + (s.completion_rate * s.total_attempts || 0), 0) / totalAttempts || 0;
  // const avgSkillSuccess = insights.reduce((sum, s) => sum + (s.skills_completion_rate * s.total_skills || 0), 0) / totalSkills || 0;
  
  // const latestPeriod = insights[insights.length - 1];
  // const previousPeriod = insights[insights.length - 2];
  
  // const skillsChange = previousPeriod 
  //   ? ((latestPeriod.total_skills - previousPeriod.total_skills) / previousPeriod.total_skills * 100).toFixed(0)
  //   : 0;

  // Prepare chart data
  const chartData = insights.map(item => ({
    period: item.period,
    hours: Math.round((item.total_minutes / 60) * 10) / 10,
    attempts: item.total_attempts,
    skills: item.total_skills,
    difficulty: item.total_difficulty,
    completion: item.completion_rate,
    skillSuccess: item.skills_completion_rate,
  }));

  const metrics = [
    { id: 'hours', label: 'Training Hours', icon: Clock, color: '#3b82f6' },
    { id: 'skills', label: 'Total Skills', icon: Target, color: '#8b5cf6' },
    { id: 'attempts', label: 'Total Attempts', icon: Activity, color: '#10b981' },
    { id: 'completion', label: 'Completion Rate', icon: Award, color: '#f59e0b' },
    { id: 'skillSuccess', label: 'Skill Success Rate', icon: Award, color: '#14b8a6' },
    { id: 'difficulty', label: 'Total Difficulty', icon: Zap, color: '#ef4444' },
  ];

  const selectedMetricData = metrics.find(m => m.id === selectedMetric);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3">
          <p className="text-sm font-semibold text-gray-900 mb-2">{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center justify-between gap-4">
              <span className="text-xs text-gray-600">{selectedMetricData.label}:</span>
              <span className="text-sm font-bold" style={{ color: entry.color }}>
                {selectedMetric === 'completion' || selectedMetric === 'skillSuccess' 
                  ? `${entry.value}%` 
                  : selectedMetric === 'hours'
                  ? `${entry.value}h`
                  : entry.value}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-3 pb-6 new-entry">
      <div className="max-w-2xl mx-auto space-y-4">
        
        {/* Header */}
        <div>
          <h2 className="text-sm text-gray-600 mt-0.5 capitalize">{discipline} Sessions • {timePeriod === 'week' ? 'Weekly' : 'Monthly'} View</h2>
        </div>

        {/* Period Toggle */}
        <div className="bg-white rounded-xl p-1 shadow-sm border border-gray-200 flex gap-1">
          <button
            onClick={() => setTimePeriod('week')}
            className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-all ${
              timePeriod === 'week'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <span className="text-lg">Weekly</span>
          </button>
          <button
            onClick={() => setTimePeriod('month')}
            className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-all ${
              timePeriod === 'month'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <span className="text-lg">Monthly</span>
          </button>
        </div>

        {/* Summary Cards */}
        <h3 className="text-lg font-semibold text-gray-600 mb-3">This Period</h3>
        <div className="grid grid-cols-2 gap-3">
          {/* <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <div className="bg-blue-100 rounded-lg p-1.5">
                <Calendar className="text-blue-600" size={16} />
              </div>
              <p className="text-base text-gray-600">Sessions</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{insights[0].count}</p>
          </div> */}

          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <div className="bg-purple-100 rounded-lg p-1.5">
                <Clock className="text-purple-600" size={16} />
              </div>
              <p className="text-base text-gray-600">Total Hours</p>
            </div>
            <div className="flex items-center gap-2">
              <p className="text-2xl font-bold text-gray-900">{(insights[0]?.total_minutes / 60).toFixed(2)}</p>
              {insights?.length > 1 && (
                  <span className={`text-xs font-semibold flex items-center gap-0.5 ${
                    (insights[0].total_minutes - insights[1].total_minutes) >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {(insights[0].total_minutes - insights[1].total_minutes) >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                    {Math.abs((insights[0].total_minutes - insights[1].total_minutes)/insights[1].total_minutes).toFixed(1) * 100}%
                  </span>
                )}
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <div className="bg-green-100 rounded-lg p-1.5">
                <Target className="text-green-600" size={16} />
              </div>
              <p className="text-base text-gray-600">Total Skills</p>
            </div>
            <div className="flex items-center gap-2">
              <p className="text-2xl font-bold text-gray-900">{insights[0]?.total_skills}</p>
              {insights?.length > 1 && (
                <span className={`text-xs font-semibold flex items-center gap-0.5 ${
                  (insights[0].total_skills - insights[1].total_skills) >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {(insights[0].total_skills - insights[1].total_skills) >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                  {Math.abs((insights[0].total_skills - insights[1].total_skills)/insights[1].total_skills).toFixed(1) * 100}%
                </span>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <div className="bg-orange-100 rounded-lg p-1.5">
                <Award className="text-orange-600" size={16} />
              </div>
              <p className="text-base text-gray-600">Completion Rate</p>
            </div>
            <div className="flex items-center gap-2">
              <p className="text-2xl font-bold text-gray-900">{insights[0]?.completion_rate.toFixed(1)}%</p>
              {insights?.length > 1 && (
                    <span className={`text-xs font-semibold flex items-center gap-0.5 ${
                      (insights[0].completion_rate - insights[1].completion_rate) >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {(insights[0].completion_rate - insights[1].completion_rate) >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                      {Math.abs((insights[0].completion_rate - insights[1].completion_rate)/insights[1].completion_rate * 100).toFixed(1)}%
                    </span>
                  )}
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <div className="bg-orange-100 rounded-lg p-1.5">
                <Award className="text-orange-600" size={16} />
              </div>
              <p className="text-base text-gray-600">Skill Completion Rate</p>
            </div>
            <div className="flex items-center gap-2">
              <p className="text-2xl font-bold text-gray-900">{insights[0]?.skills_completion_rate.toFixed(1)}%</p>
              {insights?.length > 1 && (
                <span className={`text-xs font-semibold flex items-center gap-0.5 ${
                  (insights[0].completion_rate - insights[1].completion_rate) >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {(insights[0].completion_rate - insights[1].completion_rate) >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                  {Math.abs((insights[0].completion_rate - insights[1].completion_rate)/insights[1].completion_rate).toFixed(1) * 100}%
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Metric Selector */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Select Metric to Visualise
          </label>
          <div className="relative">
            <select
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              className="w-full appearance-none bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3 pr-10 text-gray-800 font-medium focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 cursor-pointer"
              style={{ fontSize: '1rem' }}
            >
              {metrics.map(metric => (
                <option key={metric.id} value={metric.id} className="text-base">
                  {metric.label}
                </option>
              ))}
            </select>
            <ChevronDown 
              size={20} 
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none"
            />
          </div>
        </div>

        {/* Main Chart */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-2 mb-4">
            <div className="rounded-lg p-1.5" style={{ backgroundColor: `${selectedMetricData.color}20` }}>
              {<selectedMetricData.icon className="text-gray-700" size={18} style={{ color: selectedMetricData.color }} />}
            </div>
            <h3 className="text-base font-bold text-gray-900">{selectedMetricData.label} Trend</h3>
          </div>
          
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id={`color${selectedMetric}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={selectedMetricData.color} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={selectedMetricData.color} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="period" 
                tick={{ fontSize: 11 }}
                stroke="#9ca3af"
                reversed={true}
              />
              <YAxis 
                tick={{ fontSize: 11 }}
                stroke="#9ca3af"
              />
              <Tooltip content={<CustomTooltip />} />
              <Area 
                type="monotone" 
                dataKey={selectedMetric} 
                stroke={selectedMetricData.color}
                strokeWidth={3}
                fill={`url(#color${selectedMetric})`}
                dot={{ fill: selectedMetricData.color, r: 4 }}
                activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Comparison Chart - Difficulty vs Completion */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-2 mb-4">
            <div className="bg-gray-100 rounded-lg p-1.5">
              <BarChart3 className="text-gray-700" size={18} />
            </div>
            <h3 className="text-base font-bold text-gray-900">Difficulty vs Completion Rate</h3>
          </div>
          
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="period" 
                tick={{ fontSize: 11 }}
                stroke="#9ca3af"
              />
              <YAxis 
                yAxisId="left"
                tick={{ fontSize: 11 }}
                stroke="#9ca3af"
              />
              <YAxis 
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 11 }}
                stroke="#9ca3af"
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '12px'
                }}
              />
              <Legend 
                wrapperStyle={{ fontSize: '12px' }}
                iconType="circle"
              />
              <Bar 
                yAxisId="left"
                dataKey="difficulty" 
                fill="#8b5cf6" 
                name="Difficulty"
                radius={[8, 8, 0, 0]}
              />
              <Bar 
                yAxisId="right"
                dataKey="completion" 
                fill="#10b981" 
                name="Completion %"
                radius={[8, 8, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Data Table Toggle */}
        <button
          onClick={() => setShowTable(!showTable)}
          className="w-full bg-white rounded-xl p-4 shadow-sm border border-gray-200 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <span className="text-sm font-semibold text-gray-900">
            {showTable ? 'Hide' : 'Show'} Detailed Data Table
          </span>
          <ChevronDown 
            size={20} 
            className={`text-gray-600 transition-transform ${showTable ? 'rotate-180' : ''}`}
          />
        </button>

        {/* Detailed Table */}
        {showTable && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-3 text-left text-sm font-semibold text-gray-600">Period</th>
                    <th className="px-3 py-3 text-right text-sm font-semibold text-gray-600">Hours</th>
                    <th className="px-3 py-3 text-right text-sm font-semibold text-gray-600">Skills</th>
                    <th className="px-3 py-3 text-right text-sm font-semibold text-gray-600">Attempts</th>
                    <th className="px-3 py-3 text-right text-sm font-semibold text-gray-600">Difficulty</th>
                    <th className="px-3 py-3 text-right text-sm font-semibold text-gray-600">Completion</th>
                    <th className="px-3 py-3 text-right text-sm font-semibold text-gray-600">Skill Completion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {chartData.map((row, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-3 py-3 text-xs font-medium text-gray-900">{row.period}</td>
                      <td className="px-3 py-3 text-xs text-gray-600 text-right">{row.hours}h</td>
                      <td className="px-3 py-3 text-xs text-gray-600 text-right">{row.skills}</td>
                      <td className="px-3 py-3 text-xs text-gray-600 text-right">{row.attempts}</td>
                      <td className="px-3 py-3 text-xs text-gray-600 text-right">{row.difficulty}</td>
                      <td className="px-3 py-3 text-xs text-gray-600 text-right">{row.completion}%</td>
                      <td className="px-3 py-3 text-xs text-gray-600 text-right">{row.skillSuccess}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default SessionInsights;

// import { useState, useEffect } from "react";
// import axios from "axios";
// import { ToggleButton, ToggleButtonGroup, Box, FormControl, MenuItem, Select, InputLabel } from "@mui/material";
// import { DataGrid } from '@mui/x-data-grid';
// import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
// import ErrorMessage from "../../../components/ErrorMessage.js";

// const SessionInsights = ({ userId, discipline }) => {
//     const [insights, setInsights] = useState(null);
//     const [loading, setLoading] = useState(true);
//     const [timePeriod, setTimePeriod] = useState('month');
//     const [selectedAttribute, setSelectedAttribute] = useState("total_skills");
//     const [isError, setIsError] = useState(false);

//     useEffect(() => {
//         const endpoint = `/api/insights/sessions/${discipline}/${userId}?period=${timePeriod}`;
    
//         const fetchData = async () => {
//             try {
//                 setLoading(true);
//                 const response = await axios.get(process.env.REACT_APP_API_HOST + endpoint);
//                 setInsights(response.data);
//             } catch (error) {
//                 console.error('Error fetching insights:', error);
//                 setIsError(true);
//             } finally {
//                 setLoading(false);
//             }
//         };
    
//         fetchData();
//     }, [userId, discipline, timePeriod]);

    
//     if (isError) return <ErrorMessage handleConfirm={() => setIsError(false)} />;

//     if (loading) {
//         return <div>Loading...</div>;
//     }

//     // const columns = [
//     //     { field: 'date', headerName: 'Date', width: 100 },
//     //     { field: 'totalHours', headerName: 'Hours Trained', width: 120 },
//     //     { field: 'totalExercises', headerName: '# Attempts', width: 120 },
//     //     { field: 'totalSkills', headerName: '# Skills', width: 120 },
//     //     { field: 'completionRate', headerName: 'Completion Rate', width: 120 },
//     //     { field: 'skillCompletionRate', headerName: 'Skill Completion Rate', width: 120 },
//     //     { field: 'totalDifficulty', headerName: 'Difficulty', width: 120 },
//     // ];
    
//     // const rows = insights.map((item, index) => ({
//     //     id: index + 1,
//     //     date: item.period, 
//     //     totalHours: `${Math.floor(item.total_hours/60)}:${item.total_hours % 60} hrs`,
//     //     totalExercises: item.total_attempts,
//     //     totalSkills: item.total_skills,
//     //     completionRate: item.completion_rate,
//     //     skillCompletionRate: item.skills_completion_rate,
//     //     totalDifficulty: item.total_difficulty,
//     // }));

//     const attributes = [
//         {header: 'Hours trained', field: 'total_minutes'}, 
//         {header: 'Total Efforts', field: 'total_attempts'}, 
//         {header: 'Total Skills', field: 'total_skills'}, 
//         {header: 'Total Difficulty', field: 'total_difficulty'}, 
//         {header: 'Completion Rate', field: 'completion_rate'}, 
//         {header: 'Skill Success Rate', field: 'skills_completion_rate'}
//     ];
//     const sessionDates = [...new Set(insights?.map(session => session.period))];

//     const columns = [
//         { field: 'stat', headerName: 'Statistic', width: 120 },
//         ...sessionDates?.map(date => ({ 
//             field: date, 
//             headerName: date, 
//             width: 120,
//             renderCell: params => {
//                 if (params.id === 'completion_rate') {
//                     return `${params.value || 0}%`;
//                 }
//                 else if (params.row.id === 'skills_completion_rate') {
//                     return `${params.value || 0}%`;
//                 }
//                 else if (params.row.id === 'total_minutes') {
//                     return `${Math.floor(params.value/60)}:${String(params.value % 60).padStart(2, '0')} hrs`;
//                 }
//                 return params.value;
//             }
//          })),
//     ];

//     const rows = attributes.map(attribute => ({
//         id: attribute.field,
//         stat: attribute.header,
//         ...sessionDates?.reduce((acc, date) => {
//             const session = insights?.find(s => s.period === date);
//             acc[date] = session ? session[attribute.field] : 'N/A';
//             return acc;
//         }, {}),
//     }));

//     const graphData = sessionDates.map(date => ({
//         name: date,
//         value: rows.find(row => row.id === selectedAttribute)[date],
//       })).filter(attr => attr.value !== 0);

//     return (
//     <form className="new-entry">

//         <ToggleButtonGroup
//             fullWidth
//             style={{marginBottom: '10px'}}
//             color="primary"
//             value={timePeriod}
//             exclusive
//             onChange={e => setTimePeriod(e.target.value)}
//             aria-label="By week or month"
//         >
//             <ToggleButton color="primary" value={'week'} aria-label="week">By Week</ToggleButton>
//             <ToggleButton color="primary" value={'month'} aria-label="month">By Month</ToggleButton>
//         </ToggleButtonGroup>

//         <Box sx={{ height: '380px', width: '100%' }}>
//             <DataGrid
//                 rows={rows}
//                 columns={columns}
//                 hideFooter={true}
//             />
//         </Box>
//         <FormControl fullWidth style={{ margin: "16px 0" }}>
//             <InputLabel>Graph Attribute</InputLabel>
//             <Select
//                 value={selectedAttribute}
//                 onChange={e => setSelectedAttribute(e.target.value)}
//             >
//             {rows.map(row => (
//                 <MenuItem key={row.id} value={row.id}>
//                 {row.stat}
//                 </MenuItem>
//             ))}
//             </Select>
//         </FormControl>

//         <LineChart
//             width={330}
//             height={400}
//             data={graphData}
//             margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
//         >
//             <CartesianGrid strokeDasharray="3 3" />
//             <XAxis dataKey="name" />
//             <YAxis />
//             <Tooltip />
//             <Legend />
//             <Line type="monotone" dataKey="value" stroke="#8884d8" activeDot={{ r: 8 }} />
//         </LineChart>

//     </form>)
// }

// export default SessionInsights;